import { Type, type Static } from '@sinclair/typebox';
import { EmailFileUploadSchema } from '../../shared/file/file-request.schema';
import { PaginationQueryParamsSchema } from '../../shared/utils/pagination-query-params.schema';
import { TrackingOptionsSchema } from '../email/email-request.schema';
import { EmailAttendeeSchema } from '../email/email.schema';

export const CreateDraftBodySchema = Type.Object(
  {
    html: Type.Optional(Type.String({ description: 'The HTML content of the draft. Required for tracking.' })),
    plain_text: Type.Optional(Type.String({ description: 'The plain text content of the draft.' })),
    subject: Type.Optional(Type.String({ description: 'Subject of the draft.' })),
    from: Type.Optional(EmailAttendeeSchema),
    to: Type.Array(EmailAttendeeSchema, {
      description: 'List of attendees to set in `to` header. An empty list clears the header.',
    }),
    cc: Type.Optional(
      Type.Array(EmailAttendeeSchema, {
        description: 'List of attendees to set in `cc` header. An empty list clears the header.',
      }),
    ),
    bcc: Type.Optional(
      Type.Array(EmailAttendeeSchema, {
        description: 'List of attendees to set in `bcc` header. An empty list clears the header.',
      }),
    ),
    reply_to: Type.Optional(
      Type.Array(EmailAttendeeSchema, {
        description:
          'List of attendees to set in the standard `Reply-To` header. Recipients answering the email will reply to these addresses instead of `from`. To reply inside an existing conversation, use `reply_to_message_id` instead.',
      }),
    ),
    reply_to_message_id: Type.Optional(
      Type.String({
        minLength: 1,
        description: `The ID of the email message that you're replying to. For Gmail and Outlook, use the provider message ID. For IMAP, use the RFC822 Message-ID header. Outlook creates a native reply draft in the original conversation; omitted content preserves the quoted body.`,
      }),
    ),
    attachments: Type.Optional(
      Type.Array(EmailFileUploadSchema, {
        description: 'The list of file attachments to the draft. For Outlook, each attachment must be smaller than 3 MB.',
      }),
    ),
    tracking_options: Type.Optional(TrackingOptionsSchema),
  },
  { additionalProperties: false },
);

export const SendDraftParamsSchema = Type.Object({ draft_id: Type.String({ description: 'ID of the draft to send.' }) });

export const GetDraftsListQuerySchema = Type.Intersect([
  Type.Object({
    to: Type.Optional(
      Type.String({
        description: 'Filter to only return drafts with the given email address in to, cc or bcc field.',
      }),
    ),
    from: Type.Optional(Type.String({ description: 'Filter to only return drafts  with the given email address in from.' })),
    any_email: Type.Optional(
      Type.String({ description: 'Filter to only return drafts related to a comma-separated list of email addresses.' }),
    ),
  }),
  PaginationQueryParamsSchema,
]);

export const GetDraftParamsSchema = Type.Object({ draft_id: Type.String() }, { description: 'ID of the draft to retrieve.' });

export const DeleteDraftParamsSchema = Type.Object({ draft_id: Type.String() }, { description: 'ID of the draft to delete.' });

export const UpdateDraftParamsSchema = Type.Object({ draft_id: Type.String() }, { description: 'ID of the draft to update.' });

export const UpdateDraftBaseBodySchema = Type.Object(
  {
    html: Type.Optional(Type.String({ description: 'The HTML content of the draft. Required for tracking.' })),
    plain_text: Type.Optional(Type.String({ description: 'The plain text content of the draft.' })),
    subject: Type.Optional(Type.String({ description: 'Subject of the draft.' })),
    from: Type.Optional(EmailAttendeeSchema),
    to: Type.Optional(Type.Array(EmailAttendeeSchema, { description: 'List of attendees to set in `to` header.' })),
    cc: Type.Optional(Type.Array(EmailAttendeeSchema, { description: 'List of attendees to set in `cc` header.' })),
    bcc: Type.Optional(Type.Array(EmailAttendeeSchema, { description: 'List of attendees to set in `bcc` header.' })),
    reply_to: Type.Optional(
      Type.Array(EmailAttendeeSchema, {
        description:
          'List of attendees to set in the standard `Reply-To` header. Recipients answering the email will reply to these addresses instead of `from`. To reply inside an existing conversation, use `reply_to_message_id` instead.',
      }),
    ),
    reply_to_message_id: Type.Optional(
      Type.String({
        minLength: 1,
        description: `The ID of the email message that you're replying to. For Gmail, use the provider message ID. For IMAP, use the RFC822 Message-ID header. Outlook cannot change the parent of an existing draft: omit this field when updating a native reply draft to preserve its conversation. Set it when creating the draft instead.`,
      }),
    ),
    attachments: Type.Optional(
      Type.Array(EmailFileUploadSchema, {
        description:
          'The list of file attachments to the draft. Any attachment already existing will be removed and replaced by the new ones. For Outlook, each attachment must be smaller than 3 MB.',
      }),
    ),
    tracking_options: Type.Optional(TrackingOptionsSchema),
    specifics: Type.Optional(Type.Any()),
  },
  { additionalProperties: false },
);

export type CreateDraftBody = Static<typeof CreateDraftBodySchema>;
export type SendDraftParams = Static<typeof SendDraftParamsSchema>;
export type GetDraftsListQuery = Static<typeof GetDraftsListQuerySchema>;
export type GetDraftParams = Static<typeof GetDraftParamsSchema>;
export type DeleteDraftParams = Static<typeof DeleteDraftParamsSchema>;
export type UpdateDraftParams = Static<typeof UpdateDraftParamsSchema>;
export type UpdateDraftBaseBody<Specifics = object> = Omit<Static<typeof UpdateDraftBaseBodySchema>, 'specifics'> & {
  specifics?: Specifics;
};
